import { ThemeProvider, Modal } from '@workboard/wobo-ui';
import {
  AiContext,
  Event,
  EventComponent,
  EventType,
  useActionItem,
} from '@workboard/action-item';
import React, {
  FC,
  lazy,
  Suspense,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import styled from 'styled-components';
import './style.scss';
import Actions from './Actions/Actions';
import { processActionItemDeleted } from './Actions/useDeleteModal';
import { toggleBodyScrollLock } from './util/util';
import { useAiUrl } from './useAiUrl';
import { getGlobalConfig } from '../global/ConfigProvider';
import { onMessage } from '../global/onMessage';
import { NotificationSeverity } from '../notifications';
import { addToRecent } from './addToRecent';
import { useIntercomMessengerOpen } from '../../../intercom/messengerState';
import { getFeatureFlag } from '../utils/getFeatureFlag';
import { holdActivityOpen, useOkrSurfaceOpen } from '../../okr/okrActivityStack';
import {
  dismissFederatedActivityDialog,
  openFederatedActivityDialog,
} from './FederatedActivityDialog';

const LazyAiPopup = lazy(async () => {
  const actionItemModule = await import(
    /* webpackChunkName: "ActionItemDetail" */
    '@workboard/action-item'
  );
  return {
    default: actionItemModule.ActionItemDetail,
  };
});

export type CloseProps = {
  wasEdited: boolean;
};

const StyledModalWrapper = styled(Modal)`
  .MuiDialogContent-root {
    border-radius: 5px;
    ${(props: any) => props.css?.borderColor ?? ''}
  }
`;

export const ModalWrapper = ({ children }: React.PropsWithChildren) => {
  const { disableEnforceFocus, id } = useContext(AiContext);
  // MUI's focus trap otherwise steals focus from the Intercom iframe while typing
  const intercomOpen = useIntercomMessengerOpen();
  // Over the OKR Dialog or KR Side Panel (MUI modal level, 1300): the popup mounts later, so
  // it stacks above them, and its own popovers (same level, mounted later still) stay above it.
  const overOkrSurface = useOkrSurfaceOpen();
  useAiUrl(id);
  const { actionItem } = useActionItem(id);
  const { typeAttributes } = actionItem || {};
  const colors = typeAttributes?.colors ?? {};
  const wrapperCss: Record<string, string> = {};
  if (colors?.border) {
    wrapperCss.borderColor = `border-top: 5px solid ${colors.border} !important;`;
  }

  return (
    <StyledModalWrapper
      disableEnforceFocus={disableEnforceFocus || intercomOpen}
      isOpen
      title={null}
      style={{ zIndex: overOkrSurface ? 1300 : 102 }}
      removeActions
      removeClose
      css={wrapperCss}
    >
      <div
        style={{
          minWidth: '960px',
          maxWidth: '1280px',
          width: 'calc(90vw - 80px)',
          height: 'calc(90vh - 55px)',
        }}
      >
        {children}
      </div>
    </StyledModalWrapper>
  );
};

export const AiPopupApp: FC<{
  aiId: string;
  aiType?: number;
  isOpen: boolean;
  onClose: () => void;
  onEvent: (event: Event) => void;
}> = ({ aiId, aiType, isOpen, onClose, onEvent }) => {
  useEffect(() => {
    // update CSRF token on open and then periodically
    window.refreshCsrfToken(true, false);
    const csrfPolling = setInterval(() => {
      window.refreshCsrfToken(true, false);
    }, 60000);
    return () => {
      clearInterval(csrfPolling);
    };
  }, []);

  return (
    <ThemeProvider>
      <Suspense fallback={null}>
        {isOpen ? (
          <LazyAiPopup
            id={aiId}
            type={aiType}
            renderActions={(props) => <Actions {...props} />}
            onClose={onClose}
            config={getGlobalConfig()}
            wrapper={ModalWrapper}
            onMessage={onMessage}
            onEvent={onEvent}
          />
        ) : null}
      </Suspense>
    </ThemeProvider>
  );
};

// Refreshes the legacy board and My Work count after an Action changed.
const refreshBoardAfterEdit = (aiId: string) => {
  if (window.boardViewModel) {
    window.boardViewModel().aiUpdate(aiId);
  }
  if (window.backendLoadAfterSaveWithTimeOut) {
    myworkCount();
    window.backendLoadAfterSaveWithTimeOut(aiId);
  }
};

export const AiPopupAppContainer: FC<{
  aiId: string;
  aiType?: number;
  onClose?: (props: CloseProps) => void;
}> = ({ aiId, aiType, onClose }) => {
  const [ongoingEvents, setOngoingEvents] = useState<Event[]>([]);
  const createdAis = useRef<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const wasEdited = useRef(false);
  const realAiId = useRef<string>(aiId);
  const mountTime = useRef(Date.now());
  const originalPath = useRef(window.location.pathname);

  const handleEvent = useCallback(
    (event: Event) => {
      const { type } = event;
      const mutateDataEvent =
        type === EventType.Create ||
        type === EventType.Change ||
        type === EventType.Remove;

      // Track display time
      if (
        event.type === EventType.Display &&
        event.component === EventComponent.Root
      ) {
        const isFetched =
          typeof event.props === 'object' && event.props
            ? (event.props as Record<string, any>).isFetched
            : false;

        if (isFetched) {
          window.wb_track('ai_action', {
            subject: 'action_item',
            action: 'display_time',
            placement: 'action_item_detail',
            duration: Date.now() - mountTime.current,
            path: originalPath.current,
          });
        }
      }

      // Count ongoing requests
      if (mutateDataEvent) {
        setOngoingEvents((prevEvents) => [...prevEvents, event]);
      }

      // Observe for AI changes
      if (type === EventType.Save) {
        wasEdited.current = true;
        setOngoingEvents((prevEvents) => {
          const eventsCopy = [...prevEvents];
          eventsCopy.pop();
          return eventsCopy;
        });
      }

      // Observe for real id (when creatiing new AI with id=-1)
      if (
        type === EventType.Save &&
        event.entity === 'actionItem' &&
        (event as any)?.response?.data?.id
      ) {
        realAiId.current = (event as any)?.response?.data?.id;
      }

      // Observe status codes to notify about missing permissions or non-existing AI
      if (
        type === EventType.FetchError &&
        event.entity === 'actionItem' &&
        (event.values as any).id === realAiId.current
      ) {
        const error = 'error' in event ? event.error : (undefined as any);
        const statusCode = error?.response?.status;
        if (statusCode >= 400 && statusCode < 500) {
          onMessage(
            statusCode === 403
              ? "You don't have access to the requested issue"
              : 'Issue not found',
            NotificationSeverity.Error
          );
          onClose?.({
            wasEdited: false,
          });
        }
      }

      // observer ai creation. If new AI is created add it to the recent items
      if (
        (type === EventType.Save || type === EventType.Fetch) &&
        event.entity === 'actionItem'
      ) {
        const createdAiId = (event as any)?.response?.data?.id ?? (event as any)?.response?.id;
        const title = (event as any)?.response?.data?.title ?? (event as any)?.response?.title
        if (!createdAiId || createdAis.current.includes((event as any)?.response?.data?.id)) {
          return;
        }

        createdAis.current.push(createdAiId);
        addToRecent(createdAiId, title);
      }
    },
    [onClose]
  );

  const handleClose = () => {
    setIsOpen(false);
    onClose?.({
      wasEdited: wasEdited.current,
    });
  };

  useEffect(() => {
    if (!isOpen && !ongoingEvents.length) {
      if (wasEdited.current) refreshBoardAfterEdit(realAiId.current);
      // eslint-disable-next-line no-use-before-define
      closeFullPopupV1();
    }
  }, [isOpen, ongoingEvents.length]);

  return (
    <AiPopupApp
      aiId={aiId}
      aiType={aiType}
      isOpen={isOpen}
      onClose={handleClose}
      onEvent={handleEvent}
    />
  );
};

let fullPopupV1Root: ReactDOM.Root;
let releaseLegacyPopupOpen: (() => void) | undefined;
const openLegacyPopupV1 = (
  aiId: string,
  onClose: (props: CloseProps) => void,
  aiType?: number
) => {
  let modalEl = document.getElementById('ai-popup-portal-container');
  if (!modalEl) {
    modalEl = document.createElement('DIV');
    modalEl.setAttribute('id', 'ai-popup-portal-container');
    document.body.appendChild(modalEl);
  }
  releaseLegacyPopupOpen?.();
  releaseLegacyPopupOpen = holdActivityOpen();
  fullPopupV1Root = ReactDOM.createRoot(modalEl);
  fullPopupV1Root.render(<AiPopupAppContainer aiId={aiId} aiType={aiType} onClose={onClose} />);
  toggleBodyScrollLock(true);
};

const federatedEditorEnabled = () =>
  getFeatureFlag('isFederatedActivityEditorDefault') &&
  Boolean((window as any).uData);

// Pages without wbConfig or uData stay on the legacy popup.
const opensFederatedEditor = (aiId: string) =>
  federatedEditorEnabled() && /^[1-9]\d*$/.test(String(aiId));

// Same request the legacy popup makes for id -1; the server pre-creates the Activity.
const createActivityId = async (aiType?: number) => {
  const { data } = await axios.get(`${getGlobalConfig().baseApiUrl}/activity/aI`, {
    params: { id: '-1', json: true, dummyAi: 'yes', type: aiType },
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'YII-CSRF-TOKEN': window.csrfToken,
    },
  });
  const id = String(data?.model?.id ?? '');
  return /^[1-9]\d*$/.test(id) ? id : undefined;
};

const openFederatedEditor = (
  aiId: string,
  onClose: (props: CloseProps) => void,
  aiType?: number,
  created = false
) =>
  openFederatedActivityDialog(aiId, {
    onClosed: ({ wasEdited, deleted }) => {
      onClose?.({ wasEdited });
      if (deleted) processActionItemDeleted(aiId);
      else if (wasEdited) refreshBoardAfterEdit(aiId);
      // Like the legacy create popup, drop the untouched placeholder on close.
      else if (created) {
        axios
          .get(`${getGlobalConfig().baseApiUrl}/activity/clean`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          })
          .catch(() => undefined);
      }
    },
    // Legacy mount path directly, so it cannot loop back into the dialog.
    onSwitchToLegacyView: (wasEdited) => {
      // The fresh legacy popup starts with wasEdited false, so refresh for the federated edit here.
      if (wasEdited) refreshBoardAfterEdit(aiId);
      openLegacyPopupV1(aiId, onClose, aiType);
    },
  });

let creatingActivity = false;

const openFullPopupV1 = (
  aiId: string,
  onClose: (props: CloseProps) => void,
  aiType?: number
) => {
  if (opensFederatedEditor(aiId)) {
    openFederatedEditor(aiId, onClose, aiType);
    return;
  }
  if (!federatedEditorEnabled() || String(aiId) !== '-1') {
    openLegacyPopupV1(aiId, onClose, aiType);
    return;
  }
  // A second Create while the request is in flight would pre-create a second placeholder.
  if (creatingActivity) return;
  creatingActivity = true;
  createActivityId(aiType)
    .catch(() => undefined)
    .then((newId) => {
      creatingActivity = false;
      if (newId) openFederatedEditor(newId, onClose, aiType, true);
      else openLegacyPopupV1(aiId, onClose, aiType);
    });
};

const closeFullPopupV1 = () => {
  if (dismissFederatedActivityDialog()) return;
  fullPopupV1Root?.unmount();
  releaseLegacyPopupOpen?.();
  releaseLegacyPopupOpen = undefined;
  toggleBodyScrollLock(false);
};

window.openFullPopupV1 = openFullPopupV1;
window.closeFullPopupV1 = closeFullPopupV1;
